import React, { useEffect, useState } from 'react';
import { PlanInfo, fetchPlans, upgradeSubscription } from '../../services/api/subscriptionService';

interface PlanChangeModalProps {
  currentPlan: string;
  /** 呼び出し側で既にプランを選んでいる時に、そのプランの確認画面から開く */
  initialPlanSlug?: string;
  onClose: () => void;
  onSuccess: () => void;
}

type ModalStep = 'plans' | 'upgrade_confirm' | 'success';

const PlanChangeModal: React.FC<PlanChangeModalProps> = ({ currentPlan, initialPlanSlug, onClose, onSuccess }) => {
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<ModalStep>('plans');
  const [selectedPlan, setSelectedPlan] = useState<PlanInfo | null>(null);
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchPlans();
        setPlans(data.plans);

        // /plans で押したプランをそのまま確認画面へ引き継ぐ（選び直させない）。
        // 引き下げは扱わないので、上位プランの時だけ飛ばす。
        const preselected = data.plans.find((plan) => plan.slug === initialPlanSlug);
        const currentIndex = data.plans.findIndex((plan) => plan.is_current || plan.slug === currentPlan);
        if (preselected && data.plans.indexOf(preselected) > currentIndex) {
          setSelectedPlan(preselected);
          setStep('upgrade_confirm');
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'プラン一覧の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentPlan, initialPlanSlug]);

  // backend の is_current を第一に、呼び出し側が持っている最新のプランも見る
  // （契約直後など、一覧の is_current がまだ追いついていないことがある）
  const currentPlanIndex = plans.findIndex((p) => p.is_current || p.slug === currentPlan);

  const handleSelectPlan = async (plan: PlanInfo) => {
    if (plan.is_current) return;

    setSelectedPlan(plan);
    const targetIndex = plans.indexOf(plan);
    const isUpgrade = targetIndex > currentPlanIndex;

    if (isUpgrade) {
      setStep('upgrade_confirm');
      return;
    }

    // ダウングレードはここから実行しない。
    // backend の /v1/subscription/downgrade は organizations.downgrade_to に予約を書くだけで、
    // **その列を読んで実際に降格させるコードが存在しない**（Stripe も更新されない）。
    // 押せてしまうと「期間終了時に変更されます」と表示されたまま旧価格で課金され続ける。
    // 実際に効く経路は Stripe カスタマーポータル（組織管理 → 契約状況）なのでそちらへ案内する。
    setError(
      'プランの引き下げはこの画面からは行えません。組織管理 → 契約状況 のお支払い管理（Stripe）からお手続きください。'
    );
    setSelectedPlan(null);
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) return;
    setProcessing(true);
    setError(null);
    try {
      const result = await upgradeSubscription(selectedPlan.slug);
      setSuccessMessage(`${result.plan_name}にアップグレードしました`);
      setStep('success');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'アップグレードに失敗しました');
    } finally {
      setProcessing(false);
    }
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return 'お問い合わせ';
    return `¥${price.toLocaleString()}/月`;
  };

  /** -1 は無制限を表す（config/stripe.php の plan_limits と同じ約束） */
  const formatQuantity = (value: number | null) => {
    if (value === null) return '—';
    if (value < 0) return '無制限';
    return value.toLocaleString();
  };

  const renderPlanCards = () => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(plans.length, 1)}, 1fr)`, gap: 16 }}>
      {plans.map((plan) => {
        const isCurrent = plan.is_current;
        const targetIndex = plans.indexOf(plan);
        // currentPlanIndex は現在のプランが一覧に無い時 -1 になる（trial や free の組織）。
        // その場合は全プランがアップグレード扱い。handleSelectPlan と同じ式にしておかないと、
        // ボタンには「ダウングレード」と出るのに押すとアップグレードが走る。
        const isUpgrade = targetIndex > currentPlanIndex;
        const isEnterprise = plan.slug === 'enterprise';

        return (
          <div
            key={plan.slug}
            style={{
              border: isCurrent ? '2px solid #3b82f6' : '1px solid #e5e7eb',
              borderRadius: 12,
              padding: 20,
              backgroundColor: '#fff',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            {isCurrent && (
              <span
                style={{
                  position: 'absolute',
                  top: -10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 12px',
                  borderRadius: 10,
                  whiteSpace: 'nowrap',
                }}
              >
                現在のプラン
              </span>
            )}

            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, textAlign: 'center', color: '#111827' }}>
              {plan.name}
            </h3>

            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>
                {formatPrice(plan.price_monthly)}
              </span>
            </div>

            <div style={{ flex: 1, fontSize: 13, color: '#374151', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span>クレジット</span>
                <span style={{ fontWeight: 600 }}>{formatQuantity(plan.monthly_credits)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span>追加メンバー</span>
                <span style={{ fontWeight: 600 }}>{formatQuantity(plan.limits.member_limit)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>商品登録数</span>
                <span style={{ fontWeight: 600 }}>{formatQuantity(plan.limits.max_product_registrations)}</span>
              </div>
            </div>

            <button
              onClick={() => handleSelectPlan(plan)}
              disabled={isCurrent || isEnterprise || !isUpgrade}
              style={{
                width: '100%',
                padding: '8px 0',
                fontSize: 13,
                fontWeight: 600,
                border: 'none',
                borderRadius: 6,
                cursor: isCurrent || isEnterprise || !isUpgrade ? 'not-allowed' : 'pointer',
                color: '#fff',
                backgroundColor: isCurrent
                  ? '#9ca3af'
                  : isEnterprise
                    ? '#6b7280'
                    : isUpgrade
                      ? '#10b981'
                      : '#d1d5db',
              }}
            >
              {isCurrent
                ? '現在のプラン'
                : isEnterprise
                  ? 'お問い合わせ'
                  : isUpgrade
                    ? 'アップグレード'
                    : 'お支払い管理から変更'}
            </button>
          </div>
        );
      })}
    </div>
  );

  const renderUpgradeConfirm = () => (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: '#111827' }}>
        {selectedPlan?.name}にアップグレード
      </h3>
      <p style={{ margin: '0 0 8px', fontSize: 14, color: '#374151' }}>
        {formatPrice(selectedPlan?.price_monthly ?? null)}のプランに変更します。
      </p>
      <p style={{ margin: '0 0 24px', fontSize: 13, color: '#6b7280' }}>
        差額は日割りで即時請求されます。
      </p>

      {error && (
        <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{error}</p>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
        <button
          onClick={() => { setStep('plans'); setError(null); }}
          style={{
            padding: '8px 24px',
            fontSize: 13,
            fontWeight: 500,
            color: '#374151',
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          戻る
        </button>
        <button
          onClick={handleUpgrade}
          disabled={processing}
          style={{
            padding: '8px 24px',
            fontSize: 13,
            fontWeight: 600,
            color: '#fff',
            backgroundColor: processing ? '#9ca3af' : '#10b981',
            border: 'none',
            borderRadius: 6,
            cursor: processing ? 'not-allowed' : 'pointer',
          }}
        >
          {processing ? '処理中...' : 'アップグレードする'}
        </button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div style={{ textAlign: 'center', padding: '32px 0' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
      <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: '#111827' }}>
        プラン変更完了
      </h3>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: '#374151' }}>
        {successMessage}
      </p>
      <button
        onClick={onSuccess}
        style={{
          padding: '8px 32px',
          fontSize: 13,
          fontWeight: 600,
          color: '#fff',
          backgroundColor: '#3b82f6',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
        }}
      >
        閉じる
      </button>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#6b7280', fontSize: 14 }}>
          読み込み中...
        </div>
      );
    }

    if (error && step === 'plans') {
      return (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <p style={{ color: '#dc2626', fontSize: 14, marginBottom: 16 }}>{error}</p>
          <button
            onClick={onClose}
            style={{
              padding: '8px 24px',
              fontSize: 13,
              color: '#374151',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            閉じる
          </button>
        </div>
      );
    }

    switch (step) {
      case 'plans':
        return renderPlanCards();
      case 'upgrade_confirm':
        return renderUpgradeConfirm();
      case 'success':
        return renderSuccess();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== 'success') onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          padding: 32,
          maxWidth: 900,
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
            {step === 'plans' ? 'プランを選択' : step === 'success' ? '' : 'プラン変更'}
          </h2>
          {step !== 'success' && (
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 20,
                color: '#6b7280',
                cursor: 'pointer',
                padding: '4px 8px',
                lineHeight: 1,
              }}
            >
              &#10005;
            </button>
          )}
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default PlanChangeModal;
